import { integrationOptionsAtom } from '@/atoms/integrationAtoms';
import { setSidebarOpenTab, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { useTrackDependencyChanges } from '@/components/utils/auth.helpers';
import { getSpecialtyIdentifyTraits } from '@/components/utils/specialty-identify-traits';
import { CurrentUserProvider } from '@/context/current-user-context';
import { useIsProjectsEnabled, useIsSessionPageV2Enabled } from '@/features/flags';
import {
  accessAllPermissions,
  hasUpdatedAbilityAtom,
  updateAbility,
} from '@/helpers/casl/ability';
import { useCurrentEhrLaunchProfile } from '@/hooks/integrations/useCurrentEhrLaunchProfile';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryJoinedTeams } from '@/hooks/query/useQueryJoinedTeams';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { GET_TEAM_DETAILS, useQueryTeamDetails } from '@/hooks/query/useQueryTeamDetails';
import { useQueryUserForContext } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useDatabaseRegion } from '@/hooks/useDatabaseRegion';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useSidebarState } from '@/hooks/usePageShellState';
import { usePublishSourceControlSyncLifecycle } from '@/hooks/useSyncSourceControlSelections';
import { useSyncWorkspaceIdToPosthog } from '@/hooks/useSyncWorkspaceIdToPosthog';
import { getAppType } from '@/lib/analytics/legacy-sender-context';
import { setActiveTeam } from '@/lib/analytics/scribe/auth';
import { identify } from '@/lib/analytics/scribe/identify';
import { selectActiveTeamId } from '@/lib/analytics/workspace-analytics-context';
import { logout } from '@/lib/authActions';
import { isLogoutInFlight } from '@/lib/authInFlight';
import { logoutChannel } from '@/lib/logoutChannel';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { getSessionPageV2EnrollmentSource } from '@/models/analytics/features/user';
import { AuthProvider } from '@/models/authModel';
import { type RootState } from '@/store';
import { type IntegrationRequest } from '@/types';
import { setIntercomProperties } from '@/utils/identify';
import { logger } from '@/utils/logger/logger';
import { setDatadogProperties } from '@/utils/tracing';
import { useHcpVerificationIntercomStatus } from '@heidi/ads/data';
import { extractIdpSubFromAccessToken } from '@heidi/auth/tokens';
import { useCurrentWorkspace, useWorkspaceId } from '@heidi/workspace/data';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

export function FhirAuth({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { setIsExpanded: setIsSidebarOpen } = useSidebarState();

  const { token, authProvider } = useSelector((rootState: RootState) => rootState.auth);
  const { isAuthenticated, isLoading: isAuthenticationLoading } = useIsAuthenticated();

  const { data: joinedTeamsData, isFetched: isJoinedTeamsFetched } =
    useQueryJoinedTeams();

  const userEhrLaunchProfile = useCurrentEhrLaunchProfile();

  const workspaceId = useWorkspaceId();
  // Active team = the joined team in the current workspace (one team per
  // workspace), falling back to the first joined team when there's no match.
  const activeTeamId = selectActiveTeamId(joinedTeamsData?.joined_teams, workspaceId);
  const { data: teamDetails } = useQueryTeamDetails(activeTeamId ?? '');
  const userQuery = useQueryUserForContext();
  const { data: user } = userQuery;
  const databaseRegion = useDatabaseRegion();
  const hcpVerificationStatus = useHcpVerificationIntercomStatus();

  usePublishSourceControlSyncLifecycle({
    isAuthenticated,
    isAuthenticationLoading,
    hasFreshUserData: userQuery.isFetchedAfterMount,
    isUserQueryError: userQuery.isError,
  });
  // Haiku skips the visual gate. Fail-closed until the persisted id exists.
  useSyncWorkspaceIdToPosthog({
    state: workspaceId ? 'resolved' : 'pending',
  });

  const { data: billingInfo } = useQueryUserBillingInfo();
  const workspace = useCurrentWorkspace();
  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const isProjectsEnabled = useIsProjectsEnabled();
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);

  const integrationOptions = useAtomValue(integrationOptionsAtom);

  const { userRoleList, isLoadingTeamDetails } = useCombinedQueriesTeamGeneralInfo();

  useEffect(() => {
    logoutChannel.onmessage = (message) => {
      if (message.logout === true) {
        logger.info('force logout');
        if (!isLogoutInFlight()) {
          void logout();
        }
      }
    };

    return () => {
      logoutChannel.onmessage = null;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      void queryClient.invalidateQueries({
        queryKey: [
          GET_TEAM_DETAILS,
          { teamId: joinedTeamsData?.joined_teams?.at(0)?.team_id },
        ],
      });
    }
  }, [joinedTeamsData?.joined_teams, isAuthenticated, queryClient]);

  useEffect(() => {
    setSidebarOpenTab(
      authProvider !== AuthProvider.FHIR ? SidebarTab.SESSION_LIST : undefined
    );
    setIsSidebarOpen(authProvider !== AuthProvider.FHIR);
  }, [authProvider, setIsSidebarOpen]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const userTeamInfo = teamDetails?.team.team_members?.find(
      (member) => member.kinde_user_id === user?.kinde_user_id
    );

    identify({
      authID: user?.kinde_user_id || '',
      userTraits: {
        email: user?.email || '',
        user_ref_id: user?.user_ref_id,
        App: getAppType(),
        country: user?.country || '',
        region: user?.country ? countriesToRegion[user?.country] : undefined,
        given_name: '',
        family_name: '',
        phone_number: user?.phone_number || '',
        user_team_id: teamDetails?.team.id,
        user_team_name: teamDetails?.team.name,
        user_team_role: userTeamInfo?.user_role,
        user_team_plan: userTeamInfo?.tier,
        user_joined_team_date: userTeamInfo?.created_at,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
        user_created_date: user?.created_at,
        user_tier: user?.tier,
        user_role_list: userRoleList,
        team_tier: teamDetails?.team.tier,
        effective_tier:
          billingInfo?.third_party_license?.latest_tier || billingInfo?.tier,
        onboarding_selected_ehr_provider: user?.ehr_provider ?? null,
        ehr_provider: userEhrLaunchProfile?.ehr_provider,
        has_integration: Boolean(userEhrLaunchProfile),
        source: integrationOptions?.ehrName || 'Heidi',
        signup_attributed_product: user?.signup_source,
        integration_requests: user?.integration_requests as IntegrationRequest[],
        mfa_enabled: user?.user_mfa_info?.is_mfa_enabled,
        mfa_trigger_source: user?.user_mfa_info?.set_by,
        user_team_mfa_enabled: !!teamDetails?.team.mfa_info?.method,
        opted_in_experimental: user?.session_settings?.opted_in_experimental,
        session_page_v2_enrollment_source: getSessionPageV2EnrollmentSource(user),
        go_live_time_stamp: teamDetails?.team.go_live_time_stamp,
        onboarding_free_text: teamDetails?.team.onboarding_free_text,
        ...getSpecialtyIdentifyTraits(user?.specialty_id, specialties),
        other_specialty: user?.other_specialty,
        display_language: user?.language_time_settings?.display_language,
        seen_modals: user?.seen_modals,
        from: 'fhir-auth.tsx',
        evidence_access: user?.evidence_access ?? undefined,
        is_managed: !!user?.is_managed,
      },
    });

    setDatadogProperties(user?.kinde_user_id || '', {
      userTier: user?.tier,
      teamTier: teamDetails?.team.tier,
      teamId: teamDetails?.team.id,
      teamName: teamDetails?.team.name ?? null,
      workspaceId: workspace?.workspace_id ?? null,
      ehrProvider: userEhrLaunchProfile?.ehr_provider,
      hasIntegration: Boolean(userEhrLaunchProfile),
      source: integrationOptions?.ehrName || 'Heidi',
      userRefId: user?.user_ref_id,
      idpSub: token ? extractIdpSubFromAccessToken(token) : undefined,
    });
  }, [
    billingInfo,
    integrationOptions,
    specialties,
    teamDetails,
    token,
    user,
    userEhrLaunchProfile,
    userRoleList,
    workspace,
    workspaceId,
    user?.language_time_settings?.display_language,
  ]);

  useEffect(() => {
    if (!user) {
      return;
    }

    void setIntercomProperties({
      user,
      teamDetails,
      userEhrLaunch: userEhrLaunchProfile,
      integrationOptions,
      billingInfo,
      workspace,
      locale: user?.language_time_settings?.display_language,
      isSessionPageV2Enabled,
      isProjectsEnabled,
      databaseRegion,
      hcpVerificationStatus,
    });
  }, [
    billingInfo,
    integrationOptions,
    teamDetails,
    user,
    userEhrLaunchProfile,
    workspace,
    user?.language_time_settings?.display_language,
    isSessionPageV2Enabled,
    isProjectsEnabled,
    databaseRegion,
    hcpVerificationStatus,
  ]);

  useTrackDependencyChanges('fhir_identify', {
    'billingInfo?.tier': billingInfo?.tier,
    integrationOptions: integrationOptions,
    teamDetails: teamDetails,
    user: user,
    userEhrLaunchProfile: userEhrLaunchProfile,
    'billingInfo?.third_party_license?.latest_tier':
      billingInfo?.third_party_license?.latest_tier,
    userRoleList: userRoleList,
    specialties: specialties,
    databaseRegion: databaseRegion,
    component: 'FhirAuth',
  });

  useEffect(() => {
    if (teamDetails?.team) {
      setActiveTeam(teamDetails.team);
    }
  }, [teamDetails?.team]);

  const setHasUpdatedAbility = useSetAtom(hasUpdatedAbilityAtom);

  // update ability
  useEffect(() => {
    // wait until both queries are fetched
    if (!isJoinedTeamsFetched) {
      return;
    }

    if (isLoadingTeamDetails) {
      return;
    }

    // if user role list, set corresponding permissions
    if (userRoleList) {
      const uniquePermissions = Array.from(
        new Map(
          userRoleList
            .flatMap((role) => role.permissions)
            .map((permission) => [permission.code, permission])
        ).values()
      );

      updateAbility({ permissions: uniquePermissions });
      setHasUpdatedAbility(true);
    } else {
      accessAllPermissions();
    }
  }, [userRoleList, isJoinedTeamsFetched, isLoadingTeamDetails, setHasUpdatedAbility]);

  return <CurrentUserProvider userQuery={userQuery}>{children}</CurrentUserProvider>;
}

export default FhirAuth;
